import React from "react"
import { Helmet } from "react-helmet"

import usePageData from "../../hooks/usePageData"
import useSiteMetadata from "../../hooks/useSiteMetadata"
import useBrand from "../../hooks/useBrand"
import { Script } from "gatsby"

export interface HeadProps {
  locale?: string
  pageTitle?: string
  children?: React.ReactNode
  disableHelmet?: boolean
}

/**
 * Head is a generic component that will read brand data and populate it in the
 * <head> of the document. It doesn't need to do it all, as Helmet will let you
 * override in children without any issues.
 */
const Head: React.FC<HeadProps> = ({ locale = "en", pageTitle, children, disableHelmet = false }) => {
  const { brand } = usePageData()
  const { isTopResume, isTopCV, isZipJob, isTopCvFr } = useBrand()
  const { googleTagManagerCode } = useSiteMetadata()

  // Comment in when you want to perform staging testing
  // const getStagingGTMQueryParams = useCallback(() => {
  //   if (isTopResume) {
  //     return '&gtm_auth=iffp-nH3yCPAix2aJCoYQg&gtm_preview=env-539'
  //   }

  //   if (isZipJob) {
  //     return '&gtm_auth=7KN6vF2E4pQiRuM47oNfWw&gtm_preview=env-115'
  //   }
  // }, [isTopResume, isZipJob])

  return (
    <>
      {!disableHelmet && (
        <Helmet
          htmlAttributes={{
            lang: locale,
          }}
        >
          {pageTitle && <title>{pageTitle}</title>}
          {brand && brand?.favicon?.file?.url && <link rel="icon" href={brand.favicon.file.url} />}
        </Helmet>
      )}
      {isZipJob && (
        <Script
          id="dwin1-js"
          src="https://www.dwin1.com/19038.js"
          type="text/javascript"
          defer
          strategy="idle"
        ></Script>
      )}

      {/* Script for adding the `registerConsentListener` onto the window object
      The consent tag template calls this function on the Consent Initialization */}
      <Script id="window-consent-listeners">
        {`
          window.consentListeners = []
          window.registerConsentListener = (callback) => {
            window.consentListeners.push(callback)
          }
        `}
      </Script>
      {/* Production GTM Script Start */}
      <Script id="gtm-script">
        {`
          // Initial consent mode configuration
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push([
            "consent",
            "default",
            {
              ad_storage: "denied",
              ad_user_data: "denied",
              ad_personalization: "denied",
              analytics_storage: "denied"
            }
          ]);

          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', '${googleTagManagerCode}');
        `}
      </Script>
      {/* Production GTM Script End */}

      {/* Staging GTM Script Start */}
      {/* Comment in this one and comment out the prod script when you want to perform
      staging testing*/}
      {/* <Script id="gtm-script-staging">
        {`
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
              'https://www.googletagmanager.com/gtm.js?id=' +
              i +
              dl +
              '${getStagingGTMQueryParams()}' +
              '&gtm_cookies_win=x';
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', '${googleTagManagerCode}');
        `}
      </Script> */}
      {/* Staging GTM Script End */}

      {/* This script could be removed once we successfully optimize the LCP */}
      {process.env.NODE_ENV !== "production" && (
        <Script type="text/javascript" id="performance-observer-script">{`
        const LCP_SUB_PARTS = [
          'Time to first byte',
          'Resource load delay',
          'Resource load time',
          'Element render delay',
        ]

        new PerformanceObserver((list) => {
          const lcpEntry = list.getEntries().at(-1)
          const navEntry = performance.getEntriesByType('navigation')[0]
          const lcpResEntry = performance
            .getEntriesByType('resource')
            .filter((e) => e.name === lcpEntry.url)[0]

          // Ignore LCP entries that aren't images to reduce DevTools noise.
          // Comment this line out if you want to include text entries.
          if (!lcpEntry.url) return

          // Compute the start and end times of each LCP sub-part.
          // WARNING! If your LCP resource is loaded cross-origin, make sure to add
          // the 'Timing-Allow-Origin' (TAO) header to get the most accurate results.
          const ttfb = navEntry.responseStart
          const lcpRequestStart = Math.max(
            ttfb,
            // Prefer 'requestStart' (if TOA is set), otherwise use 'startTime'.
            lcpResEntry ? lcpResEntry.requestStart || lcpResEntry.startTime : 0
          )
          const lcpResponseEnd = Math.max(
            lcpRequestStart,
            lcpResEntry ? lcpResEntry.responseEnd : 0
          )
          const lcpRenderTime = Math.max(
            lcpResponseEnd,
            // Prefer 'renderTime' (if TOA is set), otherwise use 'loadTime'.
            lcpEntry ? lcpEntry.renderTime || lcpEntry.loadTime : 0
          )

          // Clear previous measures before making new ones.
          // Note: due to a bug this does not work in Chrome DevTools.
          LCP_SUB_PARTS.forEach((part) => performance.clearMeasures(part))

          // Create measures for each LCP sub-part for easier
          // visualization in the Chrome DevTools Performance panel.
          const lcpSubPartMeasures = [
            performance.measure(LCP_SUB_PARTS[0], {
              start: 0,
              end: ttfb,
            }),
            performance.measure(LCP_SUB_PARTS[1], {
              start: ttfb,
              end: lcpRequestStart,
            }),
            performance.measure(LCP_SUB_PARTS[2], {
              start: lcpRequestStart,
              end: lcpResponseEnd,
            }),
            performance.measure(LCP_SUB_PARTS[3], {
              start: lcpResponseEnd,
              end: lcpRenderTime,
            }),
          ]

          // Log helpful debug information to the console.
          console.log('LCP value: ', lcpRenderTime)
          console.log('LCP element: ', lcpEntry.element, lcpEntry.url)
          console.table(
            lcpSubPartMeasures.map((measure) => ({
              'LCP sub-part': measure.name,
              'Time (ms)': measure.duration,
              '% of LCP':
                Math.round((1000 * measure.duration) / lcpRenderTime) / 10 + '%',
            }))
          )
        }).observe({ type: 'largest-contentful-paint', buffered: true })
      `}</Script>
      )}

      {/* Impact - Begin */}
      {(isTopResume || isZipJob || isTopCV || isTopCvFr) && (
        <Script type="text/javascript" id="impact-script">
          {`
            (function(a,b,c,d,e,f,g){e['ire_o']=c;e[c]=e[c]||function(){(e[c].a=e[c].a||[]).push(arguments)};f=d.createElement(b);g=d.getElementsByTagName(b)[0];f.async=1;f.src=a;g.parentNode.insertBefore(f,g);})('https://utt.impactcdn.com/A3731510-f9bc-44f8-baa0-d78df44a8c681.js','script','ire',document,window);
          `}
        </Script>
      )}
      {/* Impact - End */}
      {children}
    </>
  )
}

export default Head
